import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Avatar,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import PhoneIcon from '@mui/icons-material/Phone';
import PersonIcon from '@mui/icons-material/Person';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AddLocationIcon from '@mui/icons-material/AddLocation';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { PackageCustomer, PhoneCall, CustomerAddress } from '../types';
import { LeftColumn, LeftSection, SectionTitle, CustomerCard, CallCard } from '../styles/StyledComponents';

// ============================================================================
// LEFT PANEL COMPONENT - Sol Sütun (CallerID / Müşteri)
// ============================================================================

export interface LeftPanelProps {
  // Incoming Call
  incomingCall: PhoneCall | null;
  isCallAnswered: boolean;
  onAnswerCall: () => void;
  onEndCall: () => void;
  
  // Customer
  currentCustomer: PackageCustomer | null;
  selectedAddressId: number | null;
  onSelectAddress: (addressId: number) => void;
  onAddCustomer: (phone: string) => void;
  onAddAddress: () => void;
  
  // Recent Calls
  recentCalls: PhoneCall[];
  customerPhoneFilter: string | null;
  onSelectRecentCall: (call: PhoneCall) => void;
  onShowAllCalls: () => void;
  
  // Address Detail
  onViewAddress?: (address: CustomerAddress) => void;
  
  // Theme
  themeColors: { primary: string; secondary: string };
}

const LeftPanel: React.FC<LeftPanelProps> = ({
  incomingCall,
  isCallAnswered,
  onAnswerCall,
  onEndCall,
  currentCustomer,
  selectedAddressId,
  onSelectAddress,
  onAddCustomer,
  onAddAddress,
  recentCalls,
  customerPhoneFilter,
  onSelectRecentCall,
  onShowAllCalls,
  onViewAddress,
  themeColors,
}) => {
  const { t } = useTranslation('salesSystem');
  return (
    <LeftColumn>
      
      {/* Gelen Arama */}
      {incomingCall && (
        <LeftSection sx={{ flex: 'none', minHeight: 'auto', pb: 1 }}>
          <SectionTitle>{t('orderManagement.leftPanel.incomingCall.title')}</SectionTitle>
          <Paper
            elevation={3}
            sx={{
              p: 1,
              bgcolor: 'success.light',
              color: 'success.contrastText',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              animation: 'pulse 1.5s infinite',
              '@keyframes pulse': {
                '0%': { opacity: 1 },
                '50%': { opacity: 0.7 },
                '100%': { opacity: 1 },
              },
            }}
          >
            <PhoneInTalkIcon fontSize="small" />
            <Box flex={1}>
              <Typography variant="body2" fontWeight="bold" fontSize="0.8rem">
                {incomingCall.phone}
              </Typography>
              <Typography variant="caption" fontSize="0.7rem">
                {incomingCall.customerName || t('orderManagement.leftPanel.incomingCall.newCaller')}
              </Typography>
            </Box>
            <Typography variant="caption" fontSize="0.7rem">{incomingCall.time}</Typography>
          </Paper>
          
          {/* Butonlar */}
          <Box display="flex" gap={0.5} mt={1}>
            {!isCallAnswered ? (
              <Button
                fullWidth
                size="small"
                variant="contained"
                color="success"
                onClick={onAnswerCall}
                sx={{ fontSize: '0.75rem', py: 0.5 }}
              >
                {t('orderManagement.leftPanel.incomingCall.answer')}
              </Button>
            ) : (
              <Button
                fullWidth
                size="small"
                variant="contained"
                color="error"
                onClick={onEndCall}
                sx={{ fontSize: '0.75rem', py: 0.5 }}
              >
                {t('orderManagement.leftPanel.incomingCall.endCall')}
              </Button>
            )}
          </Box>
          
          {/* Yeni Müşteri Ekle Butonu */}
          {!incomingCall.customerName && (
            <Button
              fullWidth
              size="small"
              variant="outlined"
              onClick={() => {
                if (!isCallAnswered) {
                  onAnswerCall();
                }
                onAddCustomer(incomingCall.phone);
              }}
              sx={{
                mt: 1,
                fontSize: '0.75rem',
                py: 0.5,
                borderColor: themeColors.primary,
                color: themeColors.primary,
                '&:hover': {
                  bgcolor: `${themeColors.primary}10`,
                  borderColor: themeColors.primary,
                },
              }}
            >
              {t('orderManagement.leftPanel.incomingCall.saveNewCustomer')}
            </Button>
          )}
        </LeftSection>
      )}

      {/* Müşteri Bilgisi */}
      <LeftSection sx={{ flex: 1 }}>
        <SectionTitle>{t('orderManagement.leftPanel.customer.title')}</SectionTitle>
        <CustomerCard 
          elevation={1} 
          sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            flex: 1, 
            overflow: 'hidden',
          }}
        >
          {currentCustomer ? (
            <Box display="flex" alignItems="center" gap={1}>
              <Avatar sx={{ bgcolor: themeColors.primary, width: 32, height: 32 }}>
                <PersonIcon sx={{ fontSize: 18 }} />
              </Avatar>
              <Box flex={1}>
                <Typography variant="body2" fontWeight="bold" fontSize="0.8rem">
                  {currentCustomer.name}
                </Typography>
                <Typography variant="caption" color="text.secondary" fontSize="0.7rem">
                  {currentCustomer.phone}
                </Typography>
              </Box>
              <Box textAlign="right">
                <Chip 
                  label={t('orderManagement.leftPanel.customer.ordersShort', { count: currentCustomer.totalOrders })} 
                  size="small" 
                  color="primary" 
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.65rem' }}
                />
              </Box>
            </Box>
          ) : (
            <Box display="flex" alignItems="center" justifyContent="center" py={2}>
              <Typography variant="body2" color="text.secondary">
                {t('orderManagement.leftPanel.customer.noSelection')}
              </Typography>
            </Box>
          )}
          <Divider sx={{ my: 0.5 }} />
          
          {/* Adresler */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <LocationOnIcon sx={{ fontSize: 14 }} /> {t('orderManagement.leftPanel.customer.addresses')}
            </Typography>
            {currentCustomer && currentCustomer.id > 0 ? (
              <Button
                size="small"
                startIcon={<AddLocationIcon sx={{ fontSize: 12 }} />}
                onClick={onAddAddress}
                sx={{ 
                  fontSize: '0.65rem', 
                  py: 0, 
                  px: 0.5,
                  minWidth: 'auto',
                  color: themeColors.primary,
                  '&:hover': { bgcolor: `${themeColors.primary}10` },
                }}
              >
                {t('orderManagement.leftPanel.customer.newAddress')}
              </Button>
            ) : currentCustomer && currentCustomer.id === 0 ? (
              <Button
                size="small"
                startIcon={<PersonIcon sx={{ fontSize: 12 }} />}
                onClick={() => onAddCustomer(currentCustomer.phone)}
                sx={{ 
                  fontSize: '0.65rem', 
                  py: 0, 
                  px: 0.5,
                  minWidth: 'auto',
                  color: 'warning.main',
                  '&:hover': { bgcolor: 'warning.light', color: 'warning.contrastText' },
                }}
              >
                {t('orderManagement.leftPanel.customer.saveCustomer')}
              </Button>
            ) : null}
          </Box>
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: 0.5, 
              flex: 1, 
              overflow: 'auto',
              '&::-webkit-scrollbar': { width: 4 },
              '&::-webkit-scrollbar-track': { bgcolor: 'grey.100', borderRadius: 2 },
              '&::-webkit-scrollbar-thumb': { bgcolor: 'grey.400', borderRadius: 2 },
              '&::-webkit-scrollbar-thumb:hover': { bgcolor: 'grey.500' },
            }}
          >
            {currentCustomer?.addresses && currentCustomer.addresses.length > 0 ? (
              currentCustomer.addresses.map((addr) => (
                <Box
                  key={addr.id}
                  onClick={() => onSelectAddress(addr.id)}
                  sx={{
                    p: 0.75,
                    borderRadius: 1,
                    cursor: 'pointer',
                    border: '1px solid',
                    borderColor: selectedAddressId === addr.id ? themeColors.primary : 'divider',
                    bgcolor: selectedAddressId === addr.id ? `${themeColors.primary}15` : 'transparent',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: themeColors.primary,
                      bgcolor: `${themeColors.primary}10`,
                    },
                  }}
                >
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Box
                      sx={{
                        width: 14,
                        height: 14,
                        borderRadius: '50%',
                        border: '2px solid',
                        borderColor: selectedAddressId === addr.id ? themeColors.primary : 'grey.400',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {selectedAddressId === addr.id && (
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: themeColors.primary,
                          }}
                        />
                      )}
                    </Box>
                    <Typography variant="caption" fontWeight="bold" fontSize="0.7rem" sx={{ flex: 1 }}>
                      {addr.label}
                    </Typography>
                    {addr.isDefault && (
                      <Chip label={t('orderManagement.leftPanel.customer.default')} size="small" color="primary" variant="outlined" sx={{ height: 14, fontSize: '0.55rem' }} />
                    )}
                    {onViewAddress && (
                      <Tooltip title={t('orderManagement.leftPanel.customer.addressDetail')} arrow>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onViewAddress(addr);
                          }}
                          sx={{ p: 0.5, ml: 0.25, minWidth: 28, minHeight: 28 }}
                        >
                          <VisibilityIcon sx={{ fontSize: 18, color: 'grey.600' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  {(addr.mahalle || addr.cadde || addr.sokak) && (
                    <Typography variant="caption" color="text.secondary" fontSize="0.6rem" sx={{ ml: 2.5, display: 'block', lineHeight: 1.3 }}>
                      {[addr.mahalle, addr.cadde, addr.sokak].filter(Boolean).join(', ')}
                    </Typography>
                  )}
                  {(addr.apartman || addr.binaNo || addr.blok || addr.kat || addr.daireNo) && (
                    <Typography variant="caption" color="text.secondary" fontSize="0.6rem" sx={{ ml: 2.5, display: 'block', lineHeight: 1.3 }}>
                      {[
                        addr.apartman && `${addr.apartman}`,
                        addr.blok && `Blok: ${addr.blok}`,
                        addr.binaNo && `No: ${addr.binaNo}`,
                        addr.kat && `Kat: ${addr.kat}`,
                        addr.daireNo && `D: ${addr.daireNo}`,
                      ].filter(Boolean).join(' | ')}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary" fontSize="0.6rem" sx={{ ml: 2.5, display: 'block', lineHeight: 1.3 }}>
                    {addr.district && addr.city ? `${addr.district}, ${addr.city}` : addr.address}
                  </Typography>
                  {addr.address && addr.district && addr.city && (
                    <Typography variant="caption" color="text.secondary" fontSize="0.6rem" sx={{ ml: 2.5, display: 'block', lineHeight: 1.3, fontStyle: 'italic' }}>
                      {addr.address}
                    </Typography>
                  )}
                </Box>
              ))
            ) : (
              <Box py={2} textAlign="center">
                {currentCustomer && currentCustomer.id === 0 ? (
                  <>
                    <Typography variant="caption" color="warning.main" fontWeight="bold">
                      {t('orderManagement.leftPanel.customer.noCustomerRegistered')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      {t('orderManagement.leftPanel.customer.saveCustomerFirst')}
                    </Typography>
                  </>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    {t('orderManagement.leftPanel.customer.noAddresses')}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </CustomerCard>
      </LeftSection>

      {/* Son Aramalar */}
      <LeftSection sx={{ flex: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <SectionTitle sx={{ mb: 0 }}>{t('orderManagement.leftPanel.recentCalls.title')}</SectionTitle>
          <Button
            size="small"
            onClick={onShowAllCalls}
            sx={{
              fontSize: 12,
              textTransform: 'none',
              color: themeColors.primary,
              '&:hover': {
                bgcolor: `${themeColors.primary}10`,
              },
            }}
          >
            {t('orderManagement.leftPanel.recentCalls.showAll')}
          </Button>
        </Box>
        <Box 
          sx={{ 
            flex: 1, 
            overflow: 'auto',
            '&::-webkit-scrollbar': { width: 4 },
            '&::-webkit-scrollbar-track': { bgcolor: 'grey.100', borderRadius: 2 },
            '&::-webkit-scrollbar-thumb': { bgcolor: 'grey.400', borderRadius: 2 },
            '&::-webkit-scrollbar-thumb:hover': { bgcolor: 'grey.500' },
          }}
        >
          {recentCalls.length > 0 ? (
            recentCalls.map((call) => (
              <CallCard 
                key={call.id}
                onClick={() => onSelectRecentCall(call)}
                sx={{
                  bgcolor: customerPhoneFilter === call.phone ? `${themeColors.primary}20` : 'transparent',
                  borderLeft: customerPhoneFilter === call.phone 
                    ? `3px solid ${themeColors.primary}` 
                    : call.isNew 
                      ? '3px solid #f44336'
                      : '3px solid #4caf50',
                  opacity: incomingCall ? 0.5 : 1,
                  pointerEvents: incomingCall ? 'none' : 'auto',
                  cursor: incomingCall ? 'not-allowed' : 'pointer',
                }}
              >
                <Avatar 
                  sx={{ 
                    width: 28, 
                    height: 28, 
                    bgcolor: customerPhoneFilter === call.phone 
                      ? themeColors.primary 
                      : call.isNew 
                        ? '#f44336'
                        : '#4caf50',
                    mr: 1,
                  }}
                >
                  <PhoneIcon sx={{ fontSize: 14 }} />
                </Avatar>
                <Box flex={1} minWidth={0}>
                  <Typography variant="body2" noWrap fontWeight={call.isNew || customerPhoneFilter === call.phone ? 'bold' : 'normal'}>
                    {call.customerName || call.phone}
                  </Typography>
                  {call.customerName && (
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {call.phone}
                    </Typography>
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {call.time}
                </Typography>
                <Chip 
                  label={call.isNew ? t('orderManagement.leftPanel.recentCalls.missed') : t('orderManagement.leftPanel.recentCalls.answered')} 
                  size="small" 
                  sx={{ 
                    ml: 0.5, 
                    height: 18, 
                    fontSize: '0.6rem',
                    bgcolor: call.isNew ? '#f44336' : '#4caf50',
                    color: 'white',
                  }} 
                />
              </CallCard>
            ))
          ) : (
            <Box py={2} textAlign="center">
              <Typography variant="caption" color="text.secondary">
                {t('orderManagement.leftPanel.recentCalls.empty')}
              </Typography>
            </Box>
          )}
        </Box>
      </LeftSection>
    </LeftColumn>
  );
};

export default LeftPanel;
